import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

// ── Anchor markers (same logic as PrivacyPolicy) ─────────────────────────────
// A line that is nothing but `<a id="some-id"></a>` is a marker, not content.
// It is pulled out of the line stream entirely before sections are built (so
// it never reaches RenderLines and prints itself as a raw tag), and gives
// the heading next to it a URL-stable id that does not have to match its
// heading text -- see PrivacyPolicy.jsx's copy of this constant for the full
// reasoning; kept identical here rather than shared because these two pages
// don't otherwise share a module to put it in.
// Accepts the straight-quote form (<a id="x"></a>), a self-closing or
// unclosed variant (<a id="x" /> or <a id="x">), and the curly/smart
// quotes Google Docs silently substitutes for straight ones -- Somebi's
// source is a Google Doc, and that autocorrect is the single most likely
// way a hand-typed marker stops matching without anyone noticing.
const ANCHOR_LINE_RE = /^\s*<a\s+id=["'\u201C\u201D\u2018\u2019]([a-z0-9-]+)["'\u201C\u201D\u2018\u2019]\s*\/?\s*>(?:\s*<\/a>)?\s*$/i;

// ── Parse markdown into sections (same logic as PrivacyPolicy) ───────────────
function parseMarkdown(md) {
  if (!md) return [];
  const lines = md.split("\n");

  const headingLines = [];
  lines.forEach((line, i) => { if (line.startsWith("## ")) headingLines.push(i); });
  const idOverride = {};
  lines.forEach((line, i) => {
    const m = line.match(ANCHOR_LINE_RE);
    if (!m) return;
    const next = headingLines.find((h) => h > i);
    const prev = [...headingLines].reverse().find((h) => h < i);
    const target = next !== undefined ? next : prev;
    // Lowercased so a marker typed as "Voice-Biometric-Data" still lands on
    // the lawyer's exact, lowercase #voice-biometric-data fragment -- URL
    // fragments and getElementById are both case-sensitive, and casing is
    // an easy slip when hand-typing a slug.
    if (target !== undefined) idOverride[target] = m[1].toLowerCase();
  });

  const sections = [];
  let current = null;
  lines.forEach((line, i) => {
    if (ANCHOR_LINE_RE.test(line)) return; // marker only, never body content
    if (line.startsWith("## ")) {
      if (current) sections.push(current);
      const title = line.slice(3).trim();
      const id = idOverride[i] || title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      current = { id, title, lines: [] };
    } else if (!line.startsWith("# ")) {
      if (current) current.lines.push(line);
    }
  });
  if (current) sections.push(current);
  return sections;
}

function RenderInline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i} style={{ color: "#1B110D" }}>{part.slice(2, -2)}</strong>
          : part
      )}
    </>
  );
}

function RenderLines({ lines }) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") { i++; continue; }
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) { items.push(lines[i].slice(2)); i++; }
      blocks.push(
        <ul key={i} style={{ margin: "0 0 14px", paddingLeft: 'var(--space-none)', listStyle: "none" }}>
          {items.map((item, j) => (
            <li key={j} style={{ display: "flex", alignItems: "flex-start", gap: 'var(--space-10)', fontSize: 15, lineHeight: 1.8, color: "var(--neutral-600)", padding: "3px 0" }}>
              <span style={{ color: "var(--feedback-warning)", fontSize: 'var(--font-size-micro)', marginTop: 'var(--space-sm)', flexShrink: 0 }}>●</span>
              <span><RenderInline text={item} /></span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    blocks.push(
      <p key={i} style={{ fontSize: 15, lineHeight: 1.85, color: "var(--neutral-600)", margin: "0 0 14px" }}>
        <RenderInline text={line} />
      </p>
    );
    i++;
  }
  return <>{blocks}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TermsOfService() {
  const [activeSection, setActiveSection] = useState(null);
  const [scrolled,      setScrolled]      = useState(false);
  const [tocOpen,       setTocOpen]       = useState(false);
  const [sections,      setSections]      = useState([]);
  const [updatedAt,     setUpdatedAt]     = useState("June 12, 2026");
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from("legal_documents")
          .select("body, updated_at")
          .eq("key", "tos")
          .maybeSingle();

        if (!error && data?.body) {
          const parsed = parseMarkdown(data.body);
          if (parsed.length > 0) {
            setSections(parsed);
            if (data.updated_at) {
              setUpdatedAt(new Date(data.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }));
            }
          }
        }
      } catch (err) {
        console.error("TermsOfService fetch error:", err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [sections]);

  // Jump to a linked section once its content actually exists in the DOM --
  // see PrivacyPolicy.jsx's copy of this effect for the full reasoning. Kept
  // here too even though nothing currently links into a ToS section by hash,
  // because the underlying bug (content loads after mount, so the browser's
  // own one-shot hash-jump fires too early) is identical on this page, and a
  // future ToS deep link would silently hit it again otherwise.
  useEffect(() => {
    if (!sections.length) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [sections]);

  return (
    <div style={{ minHeight: "100vh", background: "#FCF9F8", fontFamily: 'var(--font-body)', color: "#1B110D" }}>
      <style>{`
        * { box-sizing: border-box; }
        .tos-topbar { position: sticky; top: 0; z-index: 50; padding: 14px 40px; display: flex; align-items: center; justify-content: space-between; transition: background 0.2s, box-shadow 0.2s; background: transparent; }
        .tos-topbar.scrolled { background: rgba(252,249,248,0.96); backdrop-filter: blur(8px); box-shadow: 0 1px 0 rgba(27,17,13,0.07); }
        .tos-hero { padding: 56px 40px 48px; border-bottom: 1px solid #e8e4dc; }
        .tos-layout { display: flex; max-width: 1060px; margin: 0 auto; padding: 44px 40px 96px; gap: 56px; align-items: flex-start; }
        .tos-sidebar { width: 188px; flex-shrink: 0; position: sticky; top: 76px; max-height: calc(100vh - 100px); overflow-y: auto; }
        .tos-main { flex: 1; min-width: 0; }
        .tos-toc-mobile { display: none; }
        @media (max-width: 768px) {
          .tos-topbar { padding: 12px 20px; }
          .tos-hero { padding: 36px 20px 32px; }
          .tos-layout { padding: 28px 20px 72px; flex-direction: column; gap: 0; }
          .tos-sidebar { display: none; }
          .tos-toc-mobile { display: block; margin-bottom: 32px; }
        }
      `}</style>

      {/* Top bar */}
      <div className={`tos-topbar${scrolled ? " scrolled" : ""}`}>
        <a href="/" style={{ color: "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-md)', fontWeight: 500 }}>← Reanlo</a>
        <a href="/privacy" style={{ color: "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-base)' }}>Privacy Policy →</a>
      </div>

      {/* Hero */}
      <header className="tos-hero">
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>
          <span style={{ display: "inline-block", background: "var(--feedback-warning-bg)", color: "#b45309", fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 6, marginBottom: 'var(--space-14)', border: "1px solid var(--feedback-warning-border)" }}>Legal</span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: "clamp(1.8rem, 5vw, 2.8rem)", fontWeight: 700, color: "#1B110D", margin: "0 0 6px", lineHeight: 1.1 }}>Terms of Service</h1>
          <p style={{ fontSize: 'var(--font-size-base)', color: "var(--neutral-400)", margin: "0 0 14px", fontWeight: 500 }}>Last updated {updatedAt}</p>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: "var(--neutral-600)", maxWidth: 640, margin: "0 0 20px" }}>
            Welcome to Reanlo for Readers and Reanlo for Authors ("Reanlo," "we," "us"). These Terms of Service govern your use of Reanlo across reanloforreaders.com, reanloforauthors.com, and any related apps or services.
          </p>
          <div style={{ background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderLeft: "4px solid var(--brand-rust)", borderRadius: 'var(--radius-lg)', padding: "16px 20px", maxWidth: 640, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: 'var(--font-size-base)', color: "var(--neutral-600)", margin: 'var(--space-none)', lineHeight: 1.7 }}>
              By creating an account or using Reanlo, you agree to these Terms. If you don't agree, please don't use the service.
            </p>
          </div>
        </div>
      </header>

      <div className="tos-layout">
        {/* Mobile TOC */}
        <div className="tos-toc-mobile">
          <button onClick={() => setTocOpen(!tocOpen)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderRadius: 'var(--radius-lg)', padding: "12px 16px", fontSize: 'var(--font-size-base)', fontWeight: 600, color: "#1B110D", cursor: "pointer" }}>
            <span>📋 Table of Contents</span>
            <span style={{ transform: tocOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
          </button>
          {tocOpen && (
            <nav style={{ background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "8px 0" }}>
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} onClick={() => setTocOpen(false)} style={{ display: "block", padding: "9px 16px", color: activeSection === s.id ? "var(--brand-rust)" : "var(--neutral-600)", fontWeight: activeSection === s.id ? 600 : 400, textDecoration: "none", fontSize: 'var(--font-size-base)' }}>{s.title}</a>
              ))}
            </nav>
          )}
        </div>

        {/* Desktop sidebar */}
        <aside className="tos-sidebar">
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--neutral-400)", margin: "0 0 12px" }}>Contents</p>
          <nav>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} style={{ display: "block", color: activeSection === s.id ? "#1B110D" : "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-base)', lineHeight: 1.5, padding: "5px 0 5px 12px", marginLeft: -14, borderLeft: `2px solid ${activeSection === s.id ? "#B85C38" : "transparent"}`, fontWeight: activeSection === s.id ? 600 : 400, transition: "color 0.15s, border-color 0.15s" }}>{s.title}</a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="tos-main">
          {loading ? (
            <div style={{ padding: "60px 0", textAlign: "center", color: "var(--neutral-400)", fontSize: 'var(--font-size-md)' }}>Loading...</div>
          ) : sections.length === 0 ? (
            <div style={{ padding: "60px 0", textAlign: "center", color: "var(--neutral-400)", fontSize: 'var(--font-size-md)' }}>No content yet. Add it in the admin panel under Config → Legal Pages.</div>
          ) : (
            sections.map((s) => (
              <section key={s.id} id={s.id} style={{ paddingBottom: 44, marginBottom: 44, borderBottom: "1px solid var(--brand-sand)", scrollMarginTop: 84 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: "1.2rem", fontWeight: 700, color: "#1B110D", margin: "0 0 16px", lineHeight: 1.3 }}>{s.title}</h2>
                <RenderLines lines={s.lines} />
              </section>
            ))
          )}

          {!loading && sections.length > 0 && (
            <div style={{ display: "flex", gap: 'var(--space-lg)', alignItems: "center", paddingTop: 'var(--space-sm)' }}>
              <a href="/privacy" style={{ color: "var(--feedback-warning)", textDecoration: "none", fontSize: 'var(--font-size-md)', fontWeight: 600 }}>Privacy Policy</a>
              <span style={{ color: "var(--brand-sand)" }}>·</span>
              <a href="mailto:ehss@reanloforreaders.com" style={{ color: "var(--feedback-warning)", textDecoration: "none", fontSize: 'var(--font-size-md)', fontWeight: 600 }}>Contact Us</a>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
